import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../supabase";
import { getOrCreateConversation } from "../../services/chat";
import { useAuth } from "../../contexts/AuthContext";
import { fetchSponsoredEmployerIds } from "../../services/listingPurchases";
import { getPillColor, getPillTextColor } from "../../components/employer/dashboard/constants";

/** Matches `EnhancedHiringPipeline` stage names; maps legacy Candidates tab names. */
const PIPELINE_TAB_NAMES = new Set([
    "Applications",
    "Shortlist",
    "Interview",
    "Test",
    "Hired",
    "Rejected",
]);

const isHiredStatus = (status) => {
    const s = status?.toLowerCase();
    return s === 'hired' || s === 'offer_accepted';
};

function normalizePipelineTabName(name) {
    if (!name) return null;
    if (PIPELINE_TAB_NAMES.has(name)) return name;
    const legacy = {
        "All Candidates": "Applications",
        "Recommended Matches": "Applications",
        Application: "Applications",
        Qualified: "Shortlist",
        Offer: "Test",
        Disqualified: "Rejected",
    };
    if (legacy[name] !== undefined) return legacy[name];
    return "Applications";
}

const EmployerCandidates = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const jobId = searchParams.get('jobId');
    const tabName = searchParams.get('tabName');
    //if tabName exists then remove from url and set as selectedTab
    if (tabName) {
        searchParams.delete('tabName');
    }
    const [selectedTab, setSelectedTab] = useState(() => normalizePipelineTabName(tabName) || "Applications");
    const [currentJobPage, setCurrentJobPage] = useState(1);
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedJob, setSelectedJob] = useState(null);
    const [jobCards, setJobCards] = useState([]);
    const [jobApplicationsCount, setJobApplicationsCount] = useState({});
    const [applications, setApplications] = useState([]);
    const [statusCounts, setStatusCounts] = useState({
        'applied': 0,
        'screening': 0,
        'interview': 0,
        'assessment': 0,
        'offer': 0,
        'hired': 0,
        'rejected': 0
    });
    const [isEmployerSponsored, setIsEmployerSponsored] = useState(false);

    // Calculate tab counts based on filtered candidates (after search filter but before tab filter)
    const getTabCount = (tabName) => {
        // Defensive check: ensure candidates is an array
        if (!Array.isArray(candidates) || candidates.length === 0) {
            return 0;
        }
        
        // Apply search filter first
        const searchFiltered = candidates.filter(candidate => {
            if (!candidate) return false;
            const matchesSearch = (candidate.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (candidate.role || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (candidate.location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (Array.isArray(candidate.tags) ? candidate.tags : []).some(tag => (tag || '').toLowerCase().includes(searchTerm.toLowerCase()));
            return matchesSearch;
        });
        
        // Applications = every application row (all statuses), aligned with hiring pipeline fetch
        if (tabName === "Applications") {
            return searchFiltered.length;
        }

        if (tabName === "Shortlist") {
            return searchFiltered.filter(c => c.applicationStatus?.toLowerCase() === 'screening').length;
        }
        if (tabName === "Interview") {
            return searchFiltered.filter(c => c.applicationStatus?.toLowerCase() === 'interview').length;
        }
        if (tabName === "Test") {
            return searchFiltered.filter(c => {
                const s = c.applicationStatus?.toLowerCase();
                return s === 'assessment' || s === 'offer';
            }).length;
        }
        if (tabName === "Hired") {
            return searchFiltered.filter(c => isHiredStatus(c.applicationStatus)).length;
        }
        if (tabName === "Rejected") {
            return searchFiltered.filter(c => c.applicationStatus?.toLowerCase() === 'rejected').length;
        }

        return 0;
    };

    // Dynamic tabs based on actual candidate data and application status
    const tabs = [
        { name: "Applications", count: getTabCount("Applications"), active: selectedTab === "Applications" },
        { name: "Shortlist", count: getTabCount("Shortlist"), active: selectedTab === "Shortlist" },
        { name: "Interview", count: getTabCount("Interview"), active: selectedTab === "Interview" },
        { name: "Test", count: getTabCount("Test"), active: selectedTab === "Test" },
        { name: "Hired", count: getTabCount("Hired"), active: selectedTab === "Hired" },
        { name: "Rejected", count: getTabCount("Rejected"), active: selectedTab === "Rejected" },
    ];

    // Fetch job details if jobId is provided
    useEffect(() => {
        if (jobId) {
            fetchJobDetails();
        } else {
            setSelectedJob(null);
        }
    }, [jobId]);

    // Fetch candidates data
    useEffect(() => {
        fetchCandidates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    // Reset applications when jobId changes or is cleared
    useEffect(() => {
        if (!jobId) {
            setApplications([]);
            setStatusCounts({
                'applied': 0,
                'screening': 0,
                'interview': 0,
                'assessment': 0,
                'offer': 0,
                'hired': 0,
                'rejected': 0
            });
        }
    }, [jobId]);

    // Fetch employer's jobs
    useEffect(() => {
        fetchEmployerJobs();
    }, [user]);

    // Check if employer has sponsored listings
    useEffect(() => {
        const checkSponsoredStatus = async () => {
            if (!user?.id) return;
            try {
                const sponsoredIds = await fetchSponsoredEmployerIds();
                setIsEmployerSponsored(sponsoredIds.includes(user.id));
            } catch (err) {
                // Error checking sponsored status
            }
        };
        checkSponsoredStatus();
    }, [user]);

    const fetchJobDetails = async () => {
        try {
            const { data, error } = await supabase
                .from('internal_job_listings')
                .select('*')
                .eq('id', jobId)
                .eq('employer_id', user.id)
                .single();

            if (error) throw error;
            setSelectedJob(data);
        } catch (err) {
            // Error fetching job details
        }
    };

    const fetchEmployerJobs = async () => {
        if (!user) {
            return;
        }
        
        try {
            const { data, error } = await supabase
                .from('internal_job_listings')
                .select('*')
                .eq('employer_id', user.id)
                .neq('status', 'closed') // Exclude archived jobs
                .order('created_at', { ascending: false })
                .limit(10);

            if (error) throw error;
            
            // Sort by created_at ascending to determine which is the first (free) listing
            const sortedByCreation = [...data].sort((a, b) => 
                new Date(a.created_at) - new Date(b.created_at)
            );
            const firstJobId = sortedByCreation.length > 0 ? sortedByCreation[0].id : null;
            
            // Transform the data to match the expected format
            // Mark isSponsored: false for the first created job (free listing), true for the rest
            const transformedJobs = data.map(job => ({
                id: job.id,
                title: job.title,
                location: job.location,
                candidateCount: 0,
                applications: 0,
                isSponsored: job.id !== firstJobId // First job is free, rest are sponsored
            }));
            
            setJobCards(transformedJobs);

            // Fetch applications for these jobs to compute counts
            // Use simple counting logic matching Dashboard.jsx pattern
            const jobIds = data.map(j => j.id);
            if (jobIds.length > 0) {
                const { data: apps, error: appsError } = await supabase
                    .from('applications')
                    .select('id, job_listing_id, user_id, status')
                    .in('job_listing_id', jobIds);

                if (!appsError && apps) {
                    const counts = (apps || []).reduce((acc, app) => {
                        acc[app.job_listing_id] = (acc[app.job_listing_id] || 0) + 1;
                        return acc;
                    }, {});
                    setJobApplicationsCount(counts);
                } else if (appsError) {
                    setJobApplicationsCount({});
                }
            } else {
                setJobApplicationsCount({});
            }
        } catch (err) {
            setJobCards([]);
        }
    };

    // Compute candidate matches for each job using simple matching logic
    const computeMatchScore = (job, candidate) => {
        let score = 0;
        let total = 0;

        // Specialty/role match (40%)
        total += 40;
        const candidateRoles = candidate.profileData?.selectedRoles || [];
        if (job.specialty && candidateRoles.length > 0) {
            const match = candidateRoles.some(r =>
                String(r).toLowerCase() === String(job.specialty).toLowerCase()
            );
            if (match) score += 40;
        } else if (candidate.role && job.title) {
            // fallback on role vs title contains
            if (String(job.title).toLowerCase().includes(String(candidate.role).toLowerCase())) {
                score += 25; // partial
            }
        }

        // Location match (30%)
        if (job.location && candidate.location) {
            total += 30;
            if (String(candidate.location).toLowerCase().includes(String(job.location).toLowerCase())) {
                score += 30;
            }
        }

        // Employment type match (20%)
        const empTypes = candidate.profileData?.employmentTypes || [];
        if (job.employment_type && empTypes.length > 0) {
            total += 20;
            const matchEmp = empTypes
                .map(String)
                .map(v => v.toLowerCase())
                .includes(String(job.employment_type).toLowerCase());
            if (matchEmp) score += 20;
        }

        // Work preference presence bonus (10%) if available on job
        if (job.work_preference) {
            total += 10;
            score += 10;
        }

        const percent = total > 0 ? Math.round((score / total) * 100) : 0;
        return percent;
    };

    // Enrich job cards with dynamic candidate match counts and applications
    useEffect(() => {
        if (!jobCards || jobCards.length === 0) return;

        const enrich = async () => {
            try {
                // Fetch minimal job fields needed for match calc
                const ids = jobCards.map(j => j.id);
                const { data: jobsFull } = await supabase
                    .from('internal_job_listings')
                    .select('id, title, location, specialty, employment_type, work_preference')
                    .in('id', ids);

                const jobById = (jobsFull || []).reduce((acc, j) => { acc[j.id] = j; return acc; }, {});

                const updated = jobCards.map(card => {
                    const jf = jobById[card.id] || {};
                    // Count candidates meeting match threshold (>=80) for job card display
                    const matchCount = (Array.isArray(candidates) ? candidates : []).reduce((cnt, cand) => {
                        if (!cand) return cnt;
                        const pct = computeMatchScore(jf, cand);
                        return cnt + (pct >= 80 ? 1 : 0);
                    }, 0);
                    return {
                        ...card,
                        candidateCount: matchCount,
                        applications: jobApplicationsCount[card.id] || 0
                    };
                });

                setJobCards(updated);
            } catch (e) {
                // Fallback: still set applications if available
                setJobCards(prev => prev.map(card => ({
                    ...card,
                    applications: jobApplicationsCount[card.id] || 0
                })));
            }
        };

        enrich();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidates, jobApplicationsCount]);

    const calculateProfileCompleteness = (metadata) => {
        let score = 0;
        const checks = [
            metadata.first_name && metadata.last_name,
            metadata.location_city_country || metadata.country,
            metadata.profile_about,
            metadata.skills?.length > 0,
            metadata.work_experiences?.length > 0,
            metadata.educations?.length > 0,
            metadata.languages?.length > 0,
            metadata.linkedin_url,
            metadata.job_search_status,
            metadata.hpcsa_reg_no,
        ];
        const completedChecks = checks.filter((check) => check).length;
        score = Math.round((completedChecks / checks.length) * 100);
        return Math.max(0, Math.min(100, score));
    };

    const emptyStatusCounts = () => ({
        applied: 0,
        screening: 0,
        interview: 0,
        assessment: 0,
        offer: 0,
        hired: 0,
        rejected: 0,
    });

    const incrementStatusCounts = (counts, status) => {
        const s = (status || "applied").toLowerCase();
        if (isHiredStatus(status)) {
            counts.hired += 1;
        } else if (s === "rejected") {
            counts.rejected += 1;
        } else if (Object.prototype.hasOwnProperty.call(counts, s)) {
            counts[s] += 1;
        }
    };

    /** Same application query as `EnhancedHiringPipeline` fetchApps */
    const fetchEmployerApplications = async (employerUserId, jobListingId = null) => {
        let apps = [];

        if (jobListingId) {
            const { data, error } = await supabase
                .from("applications")
                .select("*")
                .eq("job_listing_id", jobListingId)
                .order("application_date", { ascending: false });
            if (error) throw error;
            apps = data || [];
        } else {
            const { data: employerJobs, error: employerJobsError } = await supabase
                .from("internal_job_listings")
                .select("id")
                .eq("employer_id", employerUserId)
                .neq("status", "closed");

            if (employerJobsError) throw employerJobsError;

            const jobIds = (employerJobs || []).map((j) => j.id);
            if (jobIds.length === 0) return [];

            const { data: allApps, error: allAppsError } = await supabase
                .from("applications")
                .select("*")
                .in("job_listing_id", jobIds)
                .order("application_date", { ascending: false });

            if (allAppsError) throw allAppsError;
            apps = allApps || [];
        }

        const userIds = Array.from(new Set(apps.map((a) => a.user_id).filter(Boolean)));
        let profilesByUserId = {};
        if (userIds.length > 0) {
            const { data: profiles, error: pErr } = await supabase
                .from("users_profiles")
                .select(`
                    profileid, role, email, firstname, lastname,
                    profession, location_city_country, profile_about,
                    profile_image_url, linkedinurl, jobsearchstatus,
                    skills, languages, work_experiences, educations,
                    selected_roles, employment_types,
                    hpcsa_verified, hpcsa_reg_no
                `)
                .in("profileid", userIds);
            if (!pErr && profiles) {
                profilesByUserId = profiles.reduce((acc, p) => {
                    acc[p.profileid] = p;
                    return acc;
                }, {});
            }
        }

        return apps.map((a) => ({
            ...a,
            users_profiles: profilesByUserId[a.user_id] || null,
        }));
    };

    const buildCandidateCardFromApplication = (app) => {
        const userRow = app.users_profiles;
        const userId = app.user_id;

        if (!userRow) {
            return {
                id: String(userId),
                applicationId: app.id,
                primaryJobId: app.job_listing_id,
                name: "Applicant",
                role: "Medical Professional",
                location: "Location not specified",
                matchPercentage: "0%",
                matchLabel: "Profile Match",
                tags: ["Applicant"],
                avatar: null,
                profileImageUrl: null,
                email: "",
                aboutText: "Profile information not available",
                skills: [],
                workExperiences: [],
                educations: [],
                languages: [],
                linkedinUrl: "",
                jobSearchStatus: "",
                hpcsaRegNo: "",
                hpcsaVerified: false,
                carLicense: false,
                applicationStatus: app.status || "applied",
                applicationStage: app.stage || "Applications",
                profileData: { selectedRoles: [], employmentTypes: [] },
            };
        }

        try {
            const userEmail = userRow.email || "email@example.com";
            const firstName = userRow.firstname || "";
            const lastName = userRow.lastname || "";
            const fullName =
                firstName && lastName
                    ? `${firstName} ${lastName}`
                    : firstName || lastName || "Employee User";

            const skills = userRow.skills ? JSON.parse(userRow.skills) : [];
            const languages = userRow.languages ? JSON.parse(userRow.languages) : [];
            const workExperiences = userRow.work_experiences
                ? JSON.parse(userRow.work_experiences)
                : [];
            const educations = userRow.educations ? JSON.parse(userRow.educations) : [];
            const selectedRoles = userRow.selected_roles
                ? JSON.parse(userRow.selected_roles)
                : [];

            const tags = skills
                .slice(0, 3)
                .map((skill) => (typeof skill === "string" ? skill : skill.name));

            let profileImageUrl = null;
            if (userRow.profile_image_url) {
                if (userRow.profile_image_url.startsWith("http")) {
                    profileImageUrl = userRow.profile_image_url;
                } else {
                    const { data: imageData } = supabase.storage
                        .from("profile-images")
                        .getPublicUrl(userRow.profile_image_url);
                    profileImageUrl = imageData.publicUrl;
                }
            }

            const completeness = calculateProfileCompleteness({
                first_name: firstName,
                last_name: lastName,
                profession: userRow.profession,
                location_city_country: userRow.location_city_country,
                profile_about: userRow.profile_about,
                skills,
                work_experiences: workExperiences,
                educations,
                languages,
                linkedin_url: userRow.linkedinurl,
                job_search_status: userRow.jobsearchstatus,
                hpcsa_reg_no: userRow.hpcsa_reg_no,
            });
            const matchPercentage = Math.min(100, Math.max(0, completeness));

            const hpcsaRegNo = (userRow.hpcsa_reg_no || "").trim();
            const value = userRow.hpcsa_verified;
            const hpcsaVerified =
                value === true ||
                value === "true" ||
                value === 1 ||
                value === "1" ||
                value === "t" ||
                value === "T";

            return {
                id: String(userRow.profileid),
                applicationId: app.id,
                primaryJobId: app.job_listing_id,
                name: fullName,
                role: userRow.profession || "Medical Professional",
                location: userRow.location_city_country || "Location not specified",
                matchPercentage: `${matchPercentage}%`,
                matchLabel: "Profile Match",
                tags: tags.length > 0 ? tags : ["Medical", "Healthcare", "Professional"],
                avatar: null,
                profileImageUrl,
                email: userEmail,
                aboutText: userRow.profile_about || "Profile information not available",
                skills,
                workExperiences,
                educations,
                languages,
                linkedinUrl: userRow.linkedinurl || "",
                jobSearchStatus: userRow.jobsearchstatus || "",
                hpcsaRegNo,
                hpcsaVerified,
                carLicense: false,
                applicationStatus: app.status || "applied",
                applicationStage: app.stage || "Applications",
                profileData: {
                    selectedRoles,
                    employmentTypes: userRow.employment_types
                        ? JSON.parse(userRow.employment_types)
                        : [],
                },
            };
        } catch {
            return null;
        }
    };

    const fetchCandidates = async () => {
        try {
            setLoading(true);

            if (!user?.id) {
                setCandidates([]);
                setApplications([]);
                return;
            }

            if (jobId) {
                const { data: jobData, error: jobError } = await supabase
                    .from("internal_job_listings")
                    .select("id, employer_id")
                    .eq("id", jobId)
                    .eq("employer_id", user.id)
                    .single();

                if (jobError || !jobData) {
                    setCandidates([]);
                    setApplications([]);
                    setStatusCounts(emptyStatusCounts());
                    return;
                }
            }

            const enrichedApps = await fetchEmployerApplications(user.id, jobId || null);
            setApplications(enrichedApps);

            const candidatesData = enrichedApps
                .map((app) => buildCandidateCardFromApplication(app))
                .filter(Boolean);

            const counts = emptyStatusCounts();
            enrichedApps.forEach((app) => {
                incrementStatusCounts(counts, app.status);
            });
            setStatusCounts(counts);
            setCandidates(candidatesData);
        } catch {
            setCandidates([]);
            setApplications([]);
            setStatusCounts(emptyStatusCounts());
        } finally {
            setLoading(false);
        }
    };

    const handleViewProfile = (candidateId, jobIdOverride) => {
        // Get jobId from URL if available
        const jobIdFromUrl = new URLSearchParams(window.location.search).get('jobId');
        const resolvedJobId = jobIdOverride || jobIdFromUrl;
        if (resolvedJobId) {
            navigate(`/employer/candidate/${candidateId}?jobId=${resolvedJobId}`);
        } else {
            navigate(`/employer/candidate/${candidateId}`);
        }
    };

    const handleSendMessage = async (candidateId) => {
        if (!user?.id) return;
        try {
            const convo = await getOrCreateConversation(user.id, candidateId);
            navigate('/employer/messages', { state: { openConversationId: convo.id } });
        } catch (e) {
            // Failed to start conversation
        }
    };

    const filteredCandidates = (Array.isArray(candidates) ? candidates : []).filter(candidate => {
        if (!candidate) return false;
        
        // Apply search filter
        const matchesSearch = (candidate.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (candidate.role || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (candidate.location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (Array.isArray(candidate.tags) ? candidate.tags : []).some(tag => (tag || '').toLowerCase().includes(searchTerm.toLowerCase()));
        
        if (!matchesSearch) return false;
        
        // Apply tab filter (same pipeline-style status rules with or without job filter)
        // Applications tab: show all application rows regardless of status
        if (selectedTab === "Applications") {
            return true;
        }

        const candidateStatus = candidate.applicationStatus?.toLowerCase();
        if (selectedTab === "Shortlist") {
            return candidateStatus === 'screening';
        }
        if (selectedTab === "Interview") {
            return candidateStatus === 'interview';
        }
        if (selectedTab === "Test") {
            return candidateStatus === 'assessment' || candidateStatus === 'offer';
        }
        if (selectedTab === "Hired") {
            return isHiredStatus(candidate.applicationStatus);
        }
        if (selectedTab === "Rejected") {
            return candidateStatus === 'rejected';
        }
        
        return true;
    });

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .employer-candidates-header {
                    padding: 1rem !important;
                }
                .employer-candidates-header-inner {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.75rem !important;
                }
                .employer-candidates-header-inner button {
                    width: 100% !important;
                    justify-content: flex-start !important;
                }
                .employer-candidates-content {
                    padding: 1rem !important;
                }
                .employer-candidates-job-scroll {
                    gap: 0.75rem !important;
                }
                .employer-candidates-job-card {
                    width: 16rem !important;
                }
                .employer-candidates-carousel {
                    justify-content: space-between !important;
                }
                .employer-candidates-tabs {
                    gap: 0.5rem !important;
                }
                .employer-candidates-tabs button {
                    font-size: 0.875rem !important;
                }
                .employer-candidates-search input {
                    width: 100% !important;
                    font-size: 0.875rem !important;
                }
                .employer-candidates-grid {
                    grid-template-columns: 1fr !important;
                    gap: 0.75rem !important;
                }
                .employer-candidates-card {
                    padding: 1rem !important;
                }
                .employer-candidates-card-inner {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 1rem !important;
                }
                .employer-candidates-card-actions {
                    margin-left: -40px !important;
                    width: 100% !important;
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 0.5rem !important;
                }
                .employer-candidates-card-actions button {
                    width: 100% !important;
                }
            }
        `}</style>
        <div className="min-h-screen bg-white employer-candidates-page">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 employer-candidates-header">
                <div className="flex items-center justify-between employer-candidates-header-inner">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            {selectedJob ? `Candidates for ${selectedJob.title}` : 'Job Candidates'}
                        </h1>
                        {selectedJob && (
                            <div className="mt-2 text-sm text-gray-600">
                                <span className="font-medium">{selectedJob.company_name}</span> • {selectedJob.location}
                            </div>
                        )}
                    </div>
                    {selectedJob && (
                        <button
                            onClick={() => navigate('/employer/candidates')}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                        >
                            ← Back to Applications
                        </button>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="px-6 py-6 employer-candidates-content">
                {/* Your Jobs Section */}
                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Filter by job</h2>
                    <div className="relative">
                        <div className="flex space-x-4 overflow-x-auto pb-4 employer-candidates-job-scroll">
                            {jobCards.map((job) => {
                                const isSelected = String(job.id) === jobId;
                                const targetUrl = isSelected 
                                    ? '/employer/candidates'
                                    : `/employer/candidates?jobId=${job.id}`;
                                return (
                                <div key={job.id} 
                                    onClick={() => navigate(targetUrl)}
                                    role="button"
                                    aria-pressed={isSelected}
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(targetUrl); }}
                                    className={`flex-shrink-0 w-80 bg-white border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow employer-candidates-job-card relative ${isSelected ? 'border-black' : 'border-gray-200'}`}>
                                    {isEmployerSponsored && job.isSponsored && (
                                        <span className="absolute top-3 right-2 bg-gray-900 text-white text-[9px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full">
                                            Sponsored
                                        </span>
                                    )}
                                    <div className="flex flex-col h-full">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 pr-16">{job.title}</h3>
                                        <div className="flex items-center text-sm text-gray-600 mb-2">
                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                            </svg>
                                            {job.location}
                                        </div>
                                        <div className="flex items-center justify-between mt-auto">
                                            <span className="text-sm font-semibold text-gray-900">
                                                {job.candidateCount} Candidate matches
                                            </span>
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                +{job.applications} applications
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                        
                        {/* Carousel Navigation - Only show if there are multiple pages */}
                        {(() => {
                            const jobsPerPage = 5;
                            const totalPages = Math.ceil(jobCards.length / jobsPerPage);
                            
                            if (totalPages <= 1) {
                                return null;
                            }
                            
                            return (
                                <div className="flex items-center justify-end mt-4 space-x-2 employer-candidates-carousel">
                                    <button 
                                        onClick={() => setCurrentJobPage(Math.max(1, currentJobPage - 1))}
                                        disabled={currentJobPage === 1}
                                        className={`p-2 ${currentJobPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                                        </svg>
                                    </button>
                                    <div className="flex space-x-1">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                            <button 
                                                key={page}
                                                onClick={() => setCurrentJobPage(page)}
                                                className={`w-6 h-6 rounded-full text-xs font-medium ${
                                                    currentJobPage === page ? 'bg-black text-white' : 'bg-white text-black border border-gray-300'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                    </div>
                                    <button 
                                        onClick={() => setCurrentJobPage(Math.min(totalPages, currentJobPage + 1))}
                                        disabled={currentJobPage === totalPages}
                                        className={`p-2 ${currentJobPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`}
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                                        </svg>
                                    </button>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* Candidate Pipeline Tabs */}
                <div className="mb-6">
                    <div className="flex space-x-8 overflow-x-auto pb-2 employer-candidates-tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.name}
                                onClick={() => setSelectedTab(tab.name)}
                                className={`flex items-center space-x-2 whitespace-nowrap pb-2 border-b-2 transition-colors ${
                                    tab.active
                                        ? 'text-gray-900 border-gray-900 font-semibold'
                                        : 'text-gray-500 border-transparent hover:text-gray-700'
                                }`}
                            >
                                <span>{tab.name}</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    tab.active
                                        ? 'bg-black text-white'
                                        : 'bg-gray-100 text-gray-700'
                                }`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Search Bar */}
                <div className="mb-4">
                    <div className="relative max-w-2xl employer-candidates-search">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search Talent..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Results Count */}
                <div className="mb-6">
                    <p className="text-sm text-gray-500">
                        {loading ? 'Loading candidates...' : `Showing ${filteredCandidates.length} of ${candidates.length} candidates`}
                    </p>
                </div>

                {/* Candidate Grid */}
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 employer-candidates-grid">
                        {filteredCandidates.map((candidate) => (
                        <div key={candidate.applicationId || candidate.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow employer-candidates-card">
                            <div className="flex items-start space-x-4">
                                {/* Avatar */}
                                <div className="flex-shrink-0">
                                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                                        {candidate.profileImageUrl ? (
                                            <img 
                                                src={candidate.profileImageUrl} 
                                                alt={candidate.name} 
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <span className="text-lg font-medium text-gray-500">
                                                {candidate.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between employer-candidates-card-inner">
                                        <div className="flex-1">
                                            {/* Name with Verification Tick */}
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {candidate.name}
                                                </h3>
                                                {candidate.hpcsaRegNo && typeof candidate.hpcsaRegNo === 'string' && candidate.hpcsaRegNo.trim().length > 0 && (
                                                    <img
                                                        src="/SealCheck.svg"
                                                        alt={candidate.hpcsaVerified ? "HPCSA Verified" : "HPCSA Pending Verification"}
                                                        className={`w-5 h-5 transition-all ${
                                                            candidate.hpcsaVerified 
                                                                ? '' 
                                                                : 'grayscale opacity-50'
                                                        }`}
                                                        style={{
                                                            filter: candidate.hpcsaVerified 
                                                                ? 'none' 
                                                                : 'grayscale(100%) opacity(0.5)'
                                                        }}
                                                        title={candidate.hpcsaVerified ? "HPCSA Verified" : "HPCSA Verification Pending"}
                                                    />
                                                )}
                                            </div>
                                            
                                            {/* Role */}
                                            <p className="text-sm text-gray-900 mb-1">
                                                {candidate.role}
                                            </p>
                                            
                                            {/* Location */}
                                            <p className="text-sm text-gray-600 mb-3">
                                                {candidate.location}
                                            </p>

                                            {/* Tags */}
                                            <div className="flex flex-wrap items-center gap-2 mb-4">
                                                {candidate.tags.map((tag, index) => (
                                                    <span key={index} className={`inline-flex items-center px-3 py-1 rounded-full text-xs ${getPillColor[index]} ${getPillTextColor[index]}`}>
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex-shrink-0 ml-4 flex flex-col space-y-2 employer-candidates-card-actions">
                                            <button 
                                                onClick={() => handleViewProfile(candidate.id, candidate.primaryJobId)}
                                                className="bg-black text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
                                            >
                                                View Application
                                            </button>
                                            <button 
                                                onClick={() => handleSendMessage(candidate.id)}
                                                className="bg-white text-black border border-gray-300 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors">
                                                Send message
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        ))}
                    </div>
                )}

                {/* Load More Button - TODO: Implement pagination
                <div className="mt-8 text-center">
                    <button className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors">
                        Load More Candidates
                    </button>
                </div>
                */}
            </div>
        </div>
        </>
    );
};

export default EmployerCandidates;


